/**
 * Chat Widget Toggle Controller
 * Business hours (9 AM - 6 PM weekdays): Show ZenChat, Hide Shopily AI
 * After hours & weekends: Show Shopily AI, Hide ZenChat
 */

(function() {
  // Timezone Controller Class
  class TimezoneDisplayController {
    constructor(config) {
      this.config = config;
      this.dayMap = {
        'sunday': 0,
        'monday': 1,
        'tuesday': 2,
        'wednesday': 3,
        'thursday': 4,
        'friday': 5,
        'saturday': 6
      };
    }

    getCurrentTimeInTimezone(timezone) {
      try {
        // Check for simulated time (used in testing)
        if (typeof window !== 'undefined' && window.simulatedTime) {
          console.log('⚠️ Using simulated time for testing');
          return new Date(window.simulatedTime.toLocaleString('en-US', { timeZone: timezone }));
        }
        return new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
      } catch (error) {
        console.error('Invalid timezone:', timezone);
        return new Date();
      }
    }

    parseTimeToMinutes(timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      return hours * 60 + minutes;
    }

    isWithinSchedule(currentDate, schedule) {
      const currentDay = currentDate.getDay();
      const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();

      for (const timeWindow of schedule) {
        const allowedDays = timeWindow.days.map(day => this.dayMap[day.toLowerCase()]);
        
        if (allowedDays.includes(currentDay)) {
          const startMinutes = this.parseTimeToMinutes(timeWindow.start_time);
          const endMinutes = this.parseTimeToMinutes(timeWindow.end_time);

          // Handle overnight schedules (e.g., 18:00 to 09:00)
          if (startMinutes > endMinutes) {
            // Split into two checks: start to midnight, and midnight to end
            if (currentMinutes >= startMinutes || currentMinutes <= endMinutes) {
              return true;
            }
          } else {
            // Normal schedule (e.g., 09:00 to 17:00)
            if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
              return true;
            }
          }
        }
      }

      return false;
    }

    isWithinDateRange(currentDate, dateRange) {
      if (!dateRange || (!dateRange.start_date && !dateRange.end_date)) {
        return true;
      }

      const currentTimestamp = currentDate.getTime();

      if (dateRange.start_date) {
        const startDate = new Date(dateRange.start_date);
        if (currentTimestamp < startDate.getTime()) {
          return false;
        }
      }

      if (dateRange.end_date) {
        const endDate = new Date(dateRange.end_date + 'T23:59:59');
        if (currentTimestamp > endDate.getTime()) {
          return false;
        }
      }

      return true;
    }

    shouldDisplay() {
      if (!this.config.enabled) {
        return true;
      }

      const currentTime = this.getCurrentTimeInTimezone(this.config.timezone);

      if (!this.isWithinDateRange(currentTime, this.config.date_range)) {
        return false;
      }

      return this.isWithinSchedule(currentTime, this.config.schedule);
    }
  }

  // Configuration
  const config = {
    "enabled": true,
    "timezone": "America/New_York",
    "schedule": [
      {
        "days": ["monday", "tuesday", "wednesday", "thursday", "friday"],
        "start_time": "18:00",
        "end_time": "09:00"
      },
      {
        "days": ["saturday", "sunday"],
        "start_time": "00:00",
        "end_time": "23:59"
      }
    ],
    "date_range": {
      "start_date": "2026-02-13",
      "end_date": "2026-12-31"
    }
  };

  let controller = new TimezoneDisplayController(config);
  
  // Selectors for Shopily AI chat widget
  const shoplyaiSelectors = [
    '#floating-shopping-assistant',
    '.BuildByShoplyAI',
    '.ShoplyChatDrawer',
    '.AssistantAsFloatingIcon',
    '.ChatIconHolder',
    '[id*="shoply"]',
    '[class*="shoply" i]',
    '[id*="shopping-assistant"]',
    '[class*="shopping-assistant" i]',
    'iframe[src*="shoply"]'
  ];

  // Selectors for ZenChat widget (expanded to catch all states)
  const zenchatSelectors = [
    // iframes
    'iframe#launcher[src*="zopim"]',
    'iframe#launcher[src*="zendesk"]',
    'iframe[src*="zopim"]',
    'iframe[src*="zendesk"]',
    'iframe[name*="launcher"]',
    'iframe[title*="chat"]',
    'iframe[title*="zendesk"]',
    // IDs
    '[id*="zopim"]',
    '[id*="zendesk"]',
    '[id*="launcher"]',
    '[id^="webWidget"]',
    // Classes
    '[class*="zopim"]',
    '[class*="zendesk"]',
    '[class*="zEWidget"]',
    // Data attributes
    '[data-test-id*="launcher"]',
    '[data-test-id*="widget"]',
    // Common Zendesk wrapper divs
    'div[style*="z-index: 999999"]',
    'div[aria-label*="chat"]',
    'div[aria-label*="help"]'
  ];

  let shoplyaiElements = [];
  let zenchatElements = [];
  let hideStyleAdded = false;

  function findElements(selectors) {
    const elements = [];
    
    selectors.forEach(selector => {
      try {
        const found = document.querySelectorAll(selector);
        found.forEach(el => {
          if (el.tagName === 'SCRIPT') return;
          
          const computedStyle = window.getComputedStyle(el);
          const isPositioned = computedStyle.position === 'fixed' || 
                               computedStyle.position === 'absolute' || 
                               computedStyle.position === 'relative';
          
          // More permissive check - include elements even if hidden
          // We want to find them so we can control their visibility
          if ((isPositioned || el.tagName === 'IFRAME') && !elements.includes(el)) {
            elements.push(el);
          }
        });
      } catch (e) {
        // Selector failed, skip
        console.debug('Selector failed:', selector, e.message);
      }
    });

    return elements;
  }

  function findShoplyaiElements() {
    return findElements(shoplyaiSelectors);
  }

  function findZenchatElements() {
    return findElements(zenchatSelectors);
  }

  function addHideStyles() {
    if (hideStyleAdded) return;
    
    const style = document.createElement('style');
    style.id = 'shoplyai-timezone-hide';
    style.textContent = `
      .shoplyai-timezone-hidden {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      .zenchat-timezone-hidden {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    hideStyleAdded = true;
  }

  function applyShoplyaiVisibility() {
    const shouldShowShopily = controller.shouldDisplay();
    shoplyaiElements = findShoplyaiElements();
    zenchatElements = findZenchatElements();

    addHideStyles();

    console.log(`📊 Visibility Check: Shopily=${shouldShowShopily ? 'SHOW' : 'HIDE'}, Shopily Elements=${shoplyaiElements.length}, ZenChat Elements=${zenchatElements.length}`);

    // Toggle Shopily AI visibility
    shoplyaiElements.forEach(element => {
      if (shouldShowShopily) {
        element.classList.remove('shoplyai-timezone-hidden');
        // Force the element to be visible
        element.style.display = '';
        element.style.visibility = 'visible';
        element.style.opacity = '1';
        element.style.pointerEvents = 'auto';
        console.log('✅ Showing Shopily AI element:', element.id || element.className);
      } else {
        element.classList.add('shoplyai-timezone-hidden');
        console.log('❌ Hiding Shopily AI element:', element.id || element.className);
      }
    });

    // Toggle ZenChat visibility (opposite of Shopily)
    zenchatElements.forEach(element => {
      if (shouldShowShopily) {
        // Hide ZenChat when Shopily is showing
        element.classList.add('zenchat-timezone-hidden');
        console.log('❌ Hiding ZenChat element:', element.id || element.className);
      } else {
        // Show ZenChat when Shopily is hidden
        element.classList.remove('zenchat-timezone-hidden');
        element.style.display = '';
        element.style.visibility = 'visible';
        element.style.opacity = '1';
        element.style.pointerEvents = 'auto';
        console.log('✅ Showing ZenChat element:', element.id || element.className);
      }
    });
  }

  function startMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const id = (node.id || '').toLowerCase();
            const className = (node.className || '').toString().toLowerCase();
            const src = (node.src || '').toLowerCase();
            const ariaLabel = (node.getAttribute && node.getAttribute('aria-label') || '').toLowerCase();
            
            // Check for Shopily or ZenChat elements
            if (id === 'floating-shopping-assistant' ||
                className.includes('buildbyshoplyai') ||
                className.includes('shoplycha') ||
                id.includes('zopim') || id.includes('zendesk') || id.includes('launcher') || id.includes('webwidget') ||
                className.includes('zopim') || className.includes('zendesk') || className.includes('zewidget') ||
                src.includes('zopim') || src.includes('zendesk') ||
                ariaLabel.includes('chat') || ariaLabel.includes('help')) {
              shouldCheck = true;
              console.log('🔄 Detected chat widget change:', {
                tag: node.tagName,
                id: node.id,
                className: node.className,
                src: node.src
              });
            }
          }
        });
        
        // Also check for attribute changes on existing nodes
        if (mutation.type === 'attributes' && mutation.target.nodeType === 1) {
          const target = mutation.target;
          const id = (target.id || '').toLowerCase();
          const className = (target.className || '').toString().toLowerCase();
          
          if (id.includes('zopim') || id.includes('zendesk') || id.includes('launcher') ||
              className.includes('zopim') || className.includes('zendesk')) {
            shouldCheck = true;
            console.log('🔄 Detected chat widget attribute change:', mutation.attributeName);
          }
        }
      });

      if (shouldCheck) {
        setTimeout(applyShoplyaiVisibility, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'id']
    });
  }

  function initialize() {
    const shouldShow = controller.shouldDisplay();
    const now = controller.getCurrentTimeInTimezone(config.timezone);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    console.log('╔════════════════════════════════════════════════════════╗');
    console.log('║  Chat Toggle Controller Initialized                   ║');
    console.log('╚════════════════════════════════════════════════════════╝');
    console.log('🕐 Current Time:', now.toLocaleString('en-US', { 
      timeZone: config.timezone, 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    }));
    console.log('🌍 Timezone:', config.timezone);
    console.log('📅 Schedule:');
    config.schedule.forEach((s, i) => {
      console.log(`   ${i+1}. ${s.days.join(', ')}: ${s.start_time} - ${s.end_time}`);
    });
    console.log('👁️  Shopily AI:', shouldShow ? '✅ VISIBLE' : '❌ HIDDEN');
    console.log('💬 ZenChat:', shouldShow ? '❌ HIDDEN' : '✅ VISIBLE');
    console.log('─────────────────────────────────────────────────────────');
    
    applyShoplyaiVisibility();
    setInterval(applyShoplyaiVisibility, 60000);
    startMutationObserver();
    
    setTimeout(applyShoplyaiVisibility, 1000);
    setTimeout(applyShoplyaiVisibility, 3000);
    setTimeout(applyShoplyaiVisibility, 5000);
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Update configuration function
  window.updateShoplyaiTimezone = function(newConfig) {
    Object.assign(config, newConfig);
    controller = new TimezoneDisplayController(config);
    applyShoplyaiVisibility();
  };

  // Debug function - call window.debugShoplyaiChat() in console
  window.debugShoplyaiChat = function() {
    const shouldShow = controller.shouldDisplay();
    const now = controller.getCurrentTimeInTimezone(config.timezone);
    
    console.log('╔════════════════════════════════════════════════════════╗');
    console.log('║  Chat Widget Debug Info                                ║');
    console.log('╚════════════════════════════════════════════════════════╝');
    console.log('🕐 Current Time:', now.toLocaleString('en-US', { 
      timeZone: config.timezone, 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      weekday: 'long'
    }));
    console.log('👁️  Shopily AI Should Be:', shouldShow ? '✅ VISIBLE' : '❌ HIDDEN');
    console.log('💬 ZenChat Should Be:', shouldShow ? '❌ HIDDEN' : '✅ VISIBLE');
    console.log('');
    console.log('📦 Found Elements:');
    console.log('   Shopily AI:', shoplyaiElements.length, 'elements');
    shoplyaiElements.forEach((el, i) => {
      console.log(`      ${i+1}.`, el.id || el.className || el.tagName, 
                  '- Display:', window.getComputedStyle(el).display,
                  '- Visibility:', window.getComputedStyle(el).visibility);
    });
    console.log('   ZenChat:', zenchatElements.length, 'elements');
    zenchatElements.forEach((el, i) => {
      console.log(`      ${i+1}.`, el.id || el.className || el.tagName,
                  '- Display:', window.getComputedStyle(el).display,
                  '- Visibility:', window.getComputedStyle(el).visibility);
    });
    
    // Find ALL possible chat-related elements on page
    console.log('');
    console.log('🔍 Scanning for ALL chat-related elements on page:');
    const allIframes = document.querySelectorAll('iframe');
    const allFixedElements = document.querySelectorAll('[style*="position: fixed"], [style*="position:fixed"]');
    const chatKeywords = ['chat', 'zendesk', 'zopim', 'launcher', 'widget', 'shoply', 'assistant'];
    const suspiciouElements = [];
    
    allIframes.forEach(iframe => {
      const src = (iframe.src || '').toLowerCase();
      const id = (iframe.id || '').toLowerCase();
      const name = (iframe.name || '').toLowerCase();
      if (chatKeywords.some(kw => src.includes(kw) || id.includes(kw) || name.includes(kw))) {
        suspiciouElements.push({
          type: 'iframe',
          element: iframe,
          id: iframe.id,
          src: iframe.src,
          name: iframe.name,
          display: window.getComputedStyle(iframe).display,
          visibility: window.getComputedStyle(iframe).visibility
        });
      }
    });
    
    allFixedElements.forEach(el => {
      const id = (el.id || '').toLowerCase();
      const className = (el.className || '').toString().toLowerCase();
      if (chatKeywords.some(kw => id.includes(kw) || className.includes(kw))) {
        suspiciouElements.push({
          type: 'fixed',
          element: el,
          tag: el.tagName,
          id: el.id,
          className: el.className,
          display: window.getComputedStyle(el).display,
          visibility: window.getComputedStyle(el).visibility
        });
      }
    });
    
    if (suspiciouElements.length > 0) {
      console.log('   Found', suspiciouElements.length, 'potential chat elements:');
      suspiciouElements.forEach((item, i) => {
        console.log(`      ${i+1}. [${item.type}]`, item.tag || 'IFRAME', 
                    item.id ? `#${item.id}` : '',
                    item.className ? `.${item.className}` : '',
                    item.src ? `src="${item.src.substring(0, 50)}..."` : '',
                    `\n          Display: ${item.display}, Visibility: ${item.visibility}`);
      });
    } else {
      console.log('   No suspicious elements found');
    }
    
    console.log('');
    console.log('💡 To manually trigger visibility check: applyShoplyaiVisibility()');
    console.log('💡 To scan page continuously: setInterval(window.debugShoplyaiChat, 5000)');
    console.log('─────────────────────────────────────────────────────────');
    
    return {
      shouldShowShopily: shouldShow,
      shoplyaiElements: shoplyaiElements.length,
      zenchatElements: zenchatElements.length,
      allChatElements: suspiciouElements,
      currentTime: now.toLocaleString(),
      config: config
    };
  };

  // Make applyShoplyaiVisibility available for manual testing
  window.applyShoplyaiVisibility = applyShoplyaiVisibility;
})();